import { FC, useEffect, useMemo, useState } from 'react';

import { Box, Checkbox, Grid, Typography } from '@mui/material';
import { FormikProps } from 'formik';

import { FieldInput } from 'src/components';
import { InputBaseField } from 'src/components/common/form/FormInput';
import CountrySelectInput from 'src/components/CountrySelectInput/CountrySelectInput';
import { NPIPhoneInput } from 'src/components/FormikComponents';
import { InputWithSearch } from 'src/components/InputWithSearch';
import { SearchInput } from 'src/components/SearchInput';
import { useValidateEmail } from 'src/hooks';
import { isIreland } from 'src/lib/countryConfig';
import { supportedCountries } from 'src/utils/utils';

import { Card } from '../card';

interface Step2PrimaryContactInformation {
    formik: FormikProps<any>;
    onUseForContact: (checked: boolean, prefix: string, formik: FormikProps<any>) => void;
    onFindAddress: (value: string, formValue: string) => Promise<void>;
    formattedAddress: string | null;
    postalLabel: string;
    countryCode?: string;
}

const Step2PrimaryContactInformation: FC<Step2PrimaryContactInformation> = ({
    formik,
    onUseForContact,
    onFindAddress,
    formattedAddress,
    postalLabel,
    countryCode,
}) => {
    const { onValidateEmail } = useValidateEmail();
    const [isUsePrimaryForBilling, setIsUsePrimaryForBilling] = useState(false);
    const [isUsePrimaryForService, setIsUsePrimaryForService] = useState(false);

    useEffect(() => {
        if (isUsePrimaryForBilling) {
            onUseForContact(true, 'bci_', formik);
        }
        if (isUsePrimaryForService) {
            onUseForContact(true, 'sci_', formik);
        }
    }, [
        isUsePrimaryForBilling,
        isUsePrimaryForService,
        formik.values.pci_email,
        formik.values.pci_firstName,
        formik.values.pci_lastName,
        formik.values.pci_mobilePhoneNumber,
        formik.values.pci_position,
        formik.values.pci_ra,
        formik.values.pci_ra_addressLine1,
        formik.values.pci_ra_addressLine2,
        formik.values.pci_ra_city,
        formik.values.pci_ra_country,
        formik.values.pci_ra_county,
        formik.values.pci_ra_eircode,
        formik.values.pci_telephoneNumber,
    ]);

    const { errors } = useMemo(() => {
        return formik;
    }, [formik]);

    return (
        <Card heading="Primary Contact Information">
            <Box display="flex" justifyContent="space-between" marginY="24px">
                <FieldInput
                    name="pci_firstName"
                    label="First Name"
                    boxStyling={{ maxWidth: '50%' }}
                />
                <FieldInput name="pci_lastName" label="Last Name" />
            </Box>
            <Box display="flex" justifyContent="space-between" marginBottom="24px">
                <SearchInput
                    name="pci_email"
                    label="Email Address"
                    value={formik.values.pci_email}
                    onChange={formik.handleChange}
                    error={errors?.pci_email}
                    boxStyling={{ maxWidth: '50%' }}
                    searchText="Validate"
                    onSearch={async (email) => {
                        const trimmedEmail = email.trim();
                        if (trimmedEmail) {
                            await onValidateEmail(
                                trimmedEmail,
                                () => {
                                    formik.setFieldError('pci_email', 'Please enter a valid email');
                                },
                                () => {
                                    formik.setFieldError('pci_email', null);
                                }
                            );
                        }
                    }}
                />

                <NPIPhoneInput
                    fieldName="pci_telephoneNumber"
                    label="Telephone Number"
                    placeholder="Enter Telephone Number"
                    defaultCountry={countryCode}
                    dataTestId="telephone-number-pci"
                />
            </Box>
            <Box display="flex" justifyContent="space-between" marginBottom="24px">
                <NPIPhoneInput
                    fieldName="pci_mobilePhoneNumber"
                    label="Mobile Number"
                    placeholder="Enter Mobile Number"
                    defaultCountry={countryCode}
                    dataTestId="mobile-number-pci"
                />

                <FieldInput name="pci_position" label="Position" boxStyling={{ maxWidth: '50%' }} />
            </Box>
            <Typography sx={{ fontSize: '20px', color: '#434341' }}>Registered Address</Typography>
            <Box height="2px" width="100%" margin="0px auto 24px" bgcolor="#F39E4E" />

            <Grid container spacing={3}>
                <Grid item width="100%">
                    <Grid item xs={12} md={12}>
                        <InputWithSearch
                            searchText="Search"
                            onSearch={(value) => {
                                return onFindAddress(value, 'pci_ra');
                            }}
                            placeholder="Search"
                            label="Search"
                            fieldName="registeredAddress"
                            maskedValue={formattedAddress}
                        />
                    </Grid>
                </Grid>
            </Grid>

            <Box display="flex" justifyContent="space-between" marginY="24px">
                <FieldInput
                    name="pci_ra_addressLine1"
                    label="Address Line 1"
                    boxStyling={{ maxWidth: '50%' }}
                />
                <FieldInput name="pci_ra_addressLine2" label="Address Line 2" />
            </Box>
            <Box display="flex" justifyContent="space-between" marginBottom="24px">
                <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                        <InputBaseField
                            label="Town/City"
                            name="pci_ra_city"
                            placeholder="Enter Town/City"
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <CountrySelectInput
                            setFieldValue={formik.setFieldValue}
                            fieldName="pci_ra_country"
                            filterByAlpha2Codes={supportedCountries(isIreland)}
                        />
                    </Grid>
                </Grid>
            </Box>
            <Box>
                <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                        <InputBaseField
                            label="County"
                            name="pci_ra_county"
                            placeholder="Enter County"
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <InputBaseField
                            label={postalLabel}
                            name="pci_ra_eircode"
                            placeholder={`Enter ${postalLabel}`}
                        />
                    </Grid>
                </Grid>
            </Box>
            <Box display="flex" alignItems="center" justifyContent="flex-end">
                <Typography fontSize="14px" color="#575757" fontWeight={400}>
                    Use Primary Contact Information for
                </Typography>

                <Box display="flex" alignItems="center">
                    <Checkbox
                        inputProps={{
                            'data-testid': `primary-for-billing-contact-checkbox`,
                        }}
                        onChange={(e) => {
                            setIsUsePrimaryForBilling(e.target.checked);
                        }}
                        sx={{ '& .MuiSvgIcon-root': { fontSize: 28 } }}
                    />
                    <Typography>Billing Contact</Typography>
                </Box>
                <Box display="flex" alignItems="center">
                    <Checkbox
                        inputProps={{
                            'data-testid': `primary-for-service-contact-checkbox`,
                        }}
                        onChange={(e) => {
                            setIsUsePrimaryForService(e.target.checked);
                        }}
                        sx={{ '& .MuiSvgIcon-root': { fontSize: 28 } }}
                    />
                    <Typography>Service Contact</Typography>
                </Box>
            </Box>
        </Card>
    );
};

export default Step2PrimaryContactInformation;
